import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { categoryLabels, useCategories } from '../catalog/ItemForm';
import { ConflictNotice } from '../ui/ConflictNotice';
import { QuickPicker, type PickerOption } from '../ui/QuickPicker';
import { bodyOf, draftOf, emptyDraft, HARDNESS_LABELS, reminderLead, RULES_KEY, RULES_RESOURCE, SCOPES, TRIGGER_KIND_LABELS, wireOf, type Hardness, type Rule, type RuleDraft, type TriggerKind } from './types';
import { NumberInput } from '../ui/NumberInput';
import { RowMenu } from '../ui/RowMenu';
import { FUNCTIONS_RESOURCE, PEOPLE_KEY, type FunctionSummary } from '../people/types';
import { leaveGuarded, sameValues, useUnsavedGuard, validForm } from '../ui/unsaved';
import { Notice } from '../ui/Notice';

type Found = PickerOption & { id: string; categoryId?: string | null };

/** Catalog items (or kits) for the quick picker; what the role may not see is simply not offered. */
async function searchItems(q: string, signal: AbortSignal, categoryId = ''): Promise<Found[]> {
  const inCategory = categoryId ? `&categoryId=${encodeURIComponent(categoryId)}` : '';
  const res = await api<{ items: { id: string; name: string; categoryId?: string | null }[] }>(`/api/catalog/items?q=${encodeURIComponent(q)}${inCategory}`, { signal });
  return res.items.slice(0, 15).map((i) => ({ key: i.id, id: i.id, name: i.name, details: [], categoryId: i.categoryId ?? null }));
}
async function searchKits(q: string, signal: AbortSignal): Promise<Found[]> {
  const res = await api<{ kits: { id: string; name: string }[] }>(`/api/kits?q=${encodeURIComponent(q)}`, { signal });
  return res.kits.slice(0, 15).map((k) => ({ key: k.id, id: k.id, name: k.name, details: ['zestaw'] }));
}

/** P15: the price list's services and the crew functions to choose from (empty without the right to see them). */
function useServices(enabled: boolean) {
  return useQuery({ queryKey: ['pricing', 'services'], enabled, queryFn: () => api<{ services: { id: string; name: string }[] }>('/api/pricing/services') });
}
function useFunctions(enabled: boolean) {
  return useQuery({ queryKey: [...PEOPLE_KEY, 'functions'], enabled, queryFn: () => api<{ functions: FunctionSummary[] }>('/api/people/functions') });
}

/** A segmented choice (radio buttons in the "szuflada" look). */
function Segmented<T extends string>({ legend, name, value, options, onChange, disabled }: { legend: string; name: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; disabled?: boolean }) {
  return (
    <fieldset className="segmented" disabled={disabled}>
      <legend>{legend}</legend>
      {options.map((o) => (
        <label key={o.value} className={o.value === value ? 'on' : undefined}>
          <input type="radio" name={name} value={o.value} checked={o.value === value} onChange={() => onChange(o.value)} />
          {o.label}
        </label>
      ))}
    </fieldset>
  );
}

function RuleForm({ initial, onClose }: { initial?: Rule; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { me } = useMe();
  const [base, setBase] = useState(initial);
  const [draft, setDraft] = useState<RuleDraft>(initial ? draftOf(initial) : emptyDraft());
  const [localError, setLocalError] = useState('');
  const [saved, setSaved] = useState(false);
  // M-f: a rule of „Zakres → funkcja” is shown, changed only in the setting
  const canEdit = !initial || (me !== undefined && can(me, RULES_RESOURCE, 'edit') && initial.source !== 'setting');
  const equipment = draft.triggerKind === 'equipment';
  const categories = useCategories(equipment && !!me && can(me, 'catalog.categories', 'view'));
  const set = (patch: Partial<RuleDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const reminder = draft.hardness === 'reminder';
  const service = draft.hardness === 'service';
  const crew = draft.hardness === 'crew';
  const gear = !reminder && !service && !crew;
  const services = useServices(service && !!me && can(me, 'pricing.services', 'view'));
  const functions = useFunctions(crew && !!me && can(me, FUNCTIONS_RESOURCE, 'view'));
  const scope = draft.triggerKind === 'scope';
  const [leadOpen, setLeadOpen] = useState(false);
  const leadDays = /^\d+$/.test(draft.reminderDaysBefore.trim()) ? Number(draft.reminderDaysBefore) : null;
  // rule 7a: a role that does not see the lead days neither sees nor sets them
  const showLead = !!me && fieldVisible(me, RULES_RESOURCE, 'reminderDaysBefore');

  const load = (rule: Rule) => {
    setBase(rule);
    setDraft(draftOf(rule));
  };

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => (base ? api<Rule>(`/api/rules/${base.id}`, { method: 'PATCH', body }) : api<Rule>('/api/rules', { method: 'POST', body })),
    onSuccess: async (rule) => {
      load(rule);
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: RULES_KEY });
    },
  });
  const error = save.error instanceof ApiError ? save.error : null;
  const conflict = error?.status === 409;

  function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const wire = wireOf(draft);
    if (!wire.ok) {
      setLocalError(wire.error);
      return;
    }
    setLocalError('');
    save.mutate(bodyOf(wire.rule, base));
  }

  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const guardRef = useUnsavedGuard({
    dirty: canEdit && !sameValues(draft, base ? draftOf(base) : emptyDraft()),
    save: async (el) => {
      const wire = wireOf(draft);
      if (!wire.ok) {
        setLocalError(wire.error);
        return false;
      }
      if (!validForm(el)) return false;
      await save.mutateAsync(bodyOf(wire.rule, base));
      return true;
    },
  });
  const title = base ? `Reguła: ${base.name}` : 'Nowa reguła';
  return (
    <form ref={guardRef} className="panel form wide rule-form" aria-label={title} data-feature-id="rules.edit" onSubmit={submit}>
      <h2>{title}</h2>
      {conflict && (
        <ConflictNotice
          onReload={() => {
            const current = error.current as Rule | undefined;
            void (current ? Promise.resolve(current) : api<Rule>(`/api/rules/${base!.id}`)).then(load);
            save.reset();
          }}
        />
      )}
      {error && !conflict && (
        <p className="alert" role="alert">
          {error.message}
          {fieldIssues(error).map((i) => (
            <span key={i.path + i.message}> {i.message}</span>
          ))}
        </p>
      )}
      {localError && (
        <p className="alert" role="alert">
          {localError}
        </p>
      )}
      {saved && !error && (
        <Notice onClose={() => setSaved(false)}>
          Zapisano.
        </Notice>
      )}
      <fieldset className="plain" disabled={!canEdit}>
        <label className="field">
          <span>Nazwa</span>
          <input value={draft.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="off" placeholder="np. Na każde 4 kolumny dodaj 1 statyw kolumnowy" />
        </label>
        <Segmented<Hardness>
          legend="Rodzaj"
          name="hardness"
          value={draft.hardness}
          options={(['required', 'suggested', 'reminder', 'service', 'crew'] as const).map((h) => ({ value: h, label: HARDNESS_LABELS[h] }))}
          onChange={(hardness) => set({ hardness })}
        />
        <p className="muted small">
          {draft.hardness === 'required' && 'Wymagana: pozycja dodaje się do sprzętówki sama.'}
          {draft.hardness === 'suggested' && 'Sugerowana: sprzętówka pokazuje propozycję z przyciskiem „Dodaj”.'}
          {draft.hardness === 'reminder' && 'Przypomnienie: nie dodaje sprzętu; treść pokazuje sprzętówka i karta wydarzenia (do odhaczenia).'}
          {service && 'Usługa w kosztorysie: usługa z cennika trafia sama do sekcji „Obsługa” wersji roboczej kosztorysu, na dni wydarzenia (np. Realizacja FOH → Realizacja dźwięku).'}
          {crew && 'Miejsce w obsadzie: zakładka „Obsada” pokazuje, ilu ludzi tej funkcji potrzeba, po jednym wierszu na miejsce, np. „Technik — miejsce 1”, „Technik — miejsce 2”. Nikogo nie przydziela.'}
        </p>

        <h3>Gdy w sprzętówce jest</h3>
        <label className="field">
          <span>Wyzwalacz</span>
          <select
            value={draft.triggerKind}
            onChange={(e) => set({ triggerKind: e.target.value as TriggerKind, triggerRef: '', triggerName: '', ...(e.target.value === 'scope' ? { mode: 'fixed' as const } : {}) })}
          >
            {(Object.keys(TRIGGER_KIND_LABELS) as TriggerKind[]).map((t) => (
              <option key={t} value={t}>
                {TRIGGER_KIND_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        {equipment && (
          <>
            <label className="field">
              <span>Kategoria</span>
              <select
                value={draft.categoryId}
                onChange={(e) =>
                  // another category: the device chosen in the former one no longer fits
                  set({ categoryId: e.target.value, categoryName: e.target.selectedOptions[0]?.text ?? '', triggerRef: '', triggerName: '' })
                }
              >
                <option value="">— wybierz —</option>
                {/* the stored one first when the list is not (yet) there */}
                {draft.categoryId && !(categories.data?.categories ?? []).some((c) => c.id === draft.categoryId) && <option value={draft.categoryId}>{draft.categoryName || 'Kategoria urządzenia'}</option>}
                {categoryLabels(categories.data?.categories ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <Segmented<'any' | 'one'>
              legend="Które urządzenie"
              name="device"
              value={draft.device}
              options={[
                { value: 'any', label: 'Dowolne z kategorii' },
                { value: 'one', label: 'Konkretne urządzenie' },
              ]}
              onChange={(device) => set({ device })}
            />
            {draft.device === 'one' && (
              <>
                {draft.triggerName && <p className="chosen">Wybrano: {draft.triggerName}</p>}
                {canEdit && (
                  <QuickPicker<Found>
                    key={draft.categoryId}
                    label="Pozycja wyzwalająca"
                    placeholder={draft.categoryName ? `np. ${draft.categoryName.toLowerCase()} z tej kategorii` : 'np. mikser CL5'}
                    search={(q, signal) => searchItems(q, signal, draft.categoryId)}
                    // a device found without a category chosen brings its category along
                    onPick={(o) => set({ triggerRef: o.id, triggerName: o.name, ...(draft.categoryId === '' && o.categoryId ? { categoryId: o.categoryId, categoryName: '' } : {}) })}
                  />
                )}
                {!draft.categoryId && <p className="muted small">Bez kategorii szukasz w całym katalogu.</p>}
              </>
            )}
          </>
        )}
        {draft.triggerKind === 'kit' && (
          <>
            {draft.triggerName && <p className="chosen">Wybrano: {draft.triggerName}</p>}
            {canEdit && <QuickPicker<Found> label="Zestaw wyzwalający" placeholder="np. monitory" search={searchKits} onPick={(o) => set({ triggerRef: o.id, triggerName: o.name })} />}
          </>
        )}
        {scope && (
          <label className="field">
            <span>Zakres prac</span>
            <select value={draft.triggerRef} onChange={(e) => set({ triggerRef: e.target.value, triggerName: e.target.selectedOptions[0]?.text ?? '' })}>
              <option value="">— wybierz —</option>
              {SCOPES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
        )}

        {!reminder && (
          <>
            <h3>{service ? 'Dodaj do kosztorysu' : crew ? 'Potrzeba w obsadzie' : 'Dodaj'}</h3>
            {gear && draft.effectItemName && <p className="chosen">Dodaje: {draft.effectItemName}</p>}
            {gear && canEdit && <QuickPicker<Found> label="Dodawana pozycja" placeholder="np. statyw kolumnowy" search={searchItems} onPick={(o) => set({ effectItemId: o.id, effectItemName: o.name })} />}
            {service && (
              <label className="field">
                <span>Usługa z cennika</span>
                <select value={draft.effectServiceId ?? ''} onChange={(e) => set({ effectServiceId: e.target.value || null, effectServiceName: e.target.selectedOptions[0]?.text ?? '' })}>
                  <option value="">— wybierz —</option>
                  {draft.effectServiceId && !(services.data?.services ?? []).some((x) => x.id === draft.effectServiceId) && <option value={draft.effectServiceId}>{draft.effectServiceName || 'Usługa'}</option>}
                  {(services.data?.services ?? []).map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
                <span className="hint">Usługi i ich funkcje w obsadzie: Finanse → Cenniki → Usługi.</span>
              </label>
            )}
            {crew && (
              <label className="field">
                <span>Funkcja</span>
                <select value={draft.effectFunctionId ?? ''} onChange={(e) => set({ effectFunctionId: e.target.value || null, effectFunctionName: e.target.selectedOptions[0]?.text ?? '' })}>
                  <option value="">— wybierz —</option>
                  {draft.effectFunctionId && !(functions.data?.functions ?? []).some((f) => f.id === draft.effectFunctionId) && <option value={draft.effectFunctionId}>{draft.effectFunctionName || 'Funkcja'}</option>}
                  {(functions.data?.functions ?? []).map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.name ?? 'Funkcja'}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="field-row">
              <Segmented<'fixed' | 'per'>
                legend="Sposób liczenia"
                name="mode"
                value={scope ? 'fixed' : draft.mode}
                disabled={scope}
                options={[
                  { value: 'fixed', label: 'Stała' },
                  { value: 'per', label: 'Na każde' },
                ]}
                onChange={(mode) => set({ mode })}
              />
              {draft.mode === 'per' && !scope && (
                <label className="field qty">
                  <span>Na każde (sztuk wyzwalacza)</span>
                  <NumberInput kind="integer" value={draft.perQuantity} onValueChange={(v) => set({ perQuantity: v })} />
                </label>
              )}
              <label className="field qty">
                <span>{crew ? 'Liczba osób' : 'Ilość'}</span>
                <NumberInput kind="integer" value={draft.addQuantity} onValueChange={(v) => set({ addQuantity: v })} />
              </label>
            </div>
            {scope && <p className="muted small">Zakres prac albo jest, albo go nie ma: reguła dodaje stałą ilość.</p>}
          </>
        )}

        <label className="field">
          <span>{reminder ? 'Treść przypomnienia' : 'Komunikat (opcjonalnie)'}</span>
          <textarea rows={2} value={draft.message} onChange={(e) => set({ message: e.target.value })} placeholder={reminder ? 'np. Sprawdź rider' : ''} />
        </label>
        {reminder && showLead && (
          <div className="rule-lead">
            <p className="muted small">
              Przypomnienie trafia do karty wydarzenia; powiadomienie do opiekuna wydarzenia{' '}
              <strong>{leadDays === null ? '…' : reminderLead(leadDays)}</strong>
              {leadDays !== null && leadDays > 0 && ' pierwszym dniem wydarzenia'}, jeśli nikt go nie odhaczy.
            </p>
            {canEdit && <RowMenu icon="gear" label="Termin przypomnienia" actions={[{ label: 'Zmień termin…', onSelect: () => setLeadOpen(true) }]} />}
          </div>
        )}
        {reminder && showLead && (leadOpen || leadDays === null) && (
          <label className="field qty">
            <span>Dni przed wydarzeniem</span>
            <NumberInput kind="integer" value={draft.reminderDaysBefore} onValueChange={(v) => set({ reminderDaysBefore: v })} />
          </label>
        )}
        <label className="check">
          <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} /> Aktywna
        </label>
      </fieldset>
      <div className="actions">
        {canEdit && (
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : base ? 'Zapisz zmiany' : 'Dodaj regułę'}
          </button>
        )}
        <button className="btn" type="button" onClick={(e) => leaveGuarded(onClose, e.currentTarget.closest('form'))}>
          Zamknij
        </button>
      </div>
    </form>
  );
}

/** Add (no `ruleId`) or edit a rule. */
export function RuleEditor({ ruleId, onClose }: { ruleId?: string; onClose: () => void }) {
  const detail = useQuery({ queryKey: [...RULES_KEY, 'rule', ruleId], enabled: ruleId !== undefined, gcTime: 0, queryFn: () => api<Rule>(`/api/rules/${ruleId}`) });
  if (ruleId === undefined) return <RuleForm onClose={onClose} />;
  if (detail.error) {
    return (
      <p className="alert" role="alert">
        {detail.error instanceof ApiError ? detail.error.message : 'Nie udało się wczytać reguły'}
      </p>
    );
  }
  if (!detail.data) return <p className="muted" role="status">Wczytywanie…</p>;
  return <RuleForm key={detail.data.id} initial={detail.data} onClose={onClose} />;
}
